import { PAGINATION_INITIAL_VALUES } from '@/assets/js/config/client'
import { STOREMODULE_ENDPOINTMODULE_MAP } from '@/assets/js/config/server'

import http from '@/$plugins/http'
import Vue from 'vue'

import i18n from '@/$plugins/i18n/core'
import base from './index'

import { normalize, unwrap } from '@/assets/js/helper/entity'
import {
  filterControlResetter,
  filterIdGenerator,
  filterMapper,
  filterParameters,
  getFilterTypeFromSearchNext,
  getFilterTypeToSearchNext,
  getSortSearchNext,
  initialParameterSetter,
  searchParameters,
  sortIdGenerator,
  sortMapper,
  urlParameterKeySetter,
  urlParameterSetter
} from '@/assets/js/helper/search'

export default {
  namespaced: true,
  state: {
    filters: [],
    sorts: [],
    hitColumns: [],
    decreasedEntities: [],
    decreasedEntitiesAllEverLoaded: []
  },
  getters: {
    filters: state => state.filters,
    sorts: state => state.sorts,
    hitColumns: state => state.hitColumns,
    getActiveSort: state => state.sorts.find(s => s.isActive),
    getDecreasedEntities: state => state.decreasedEntities,
    getAllEverLoadedDecreasedEntities: state => state.decreasedEntitiesAllEverLoaded
  },
  mutations: {
    setFilters (state, filters = []) {
      filters
        .forEach(filter => {
          const FILTER_ID = filterIdGenerator(filter)
          const updateIndex = state.filters.findIndex(f => f.id === FILTER_ID)
          const newIndex = state.filters.length
          const existingFilter = state.filters[updateIndex] || {}

          Vue.set(state.filters, updateIndex >= 0 ? updateIndex : newIndex, filterMapper(filter, existingFilter, state.urlParameters))
        })

      state.filters.sort((a, b) => a.sortOrder - b.sortOrder)
    },
    setSorts (state, sorts = []) {
      sorts
        .forEach(sort => {
          const SORT_ID = sortIdGenerator(sort)
          const updateIndex = state.sorts.findIndex(s => s.id === SORT_ID)
          const newIndex = state.sorts.length
          const existingSort = state.sorts[updateIndex] || {}

          Vue.set(state.sorts, updateIndex >= 0 ? updateIndex : newIndex, sortMapper(sort, existingSort))
        })

      state.sorts.sort((a, b) => a.sortOrder - b.sortOrder)
    },
    setHitColumns (state, hitColumns = []) {
      state.hitColumns = hitColumns.sort((a, b) => a.sortOrder - b.sortOrder)
    },
    append (state, { key, entities = [] }) {
      state.entities = state.entities
        .concat(entities
          .map(entity => {
            const normalizedEntity = normalize(entity, key)
            if (normalizedEntity.value.id === state.self.value.id) state.self = normalizedEntity
            return normalizedEntity
          })
        )
    },
    setDecreasedEntities (state, entities = []) {
      state.decreasedEntities = entities.map(unwrap)
      state.decreasedEntitiesAllEverLoaded = state.decreasedEntitiesAllEverLoaded.concat(state.decreasedEntities)
    }
  },
  actions: {
    initSearch (serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, { filters = [] }) {
        initialParameterSetter(filters)
        return dispatch('searchByFilters')
      }
    },
    searchByFilters (serverModuleName = '', serverEntityName = '', entityKey = '') {
      const isKey = `${serverEntityName}/searchByFilters`

      return function ({ state, getters, commit, dispatch, rootGetters }, options = {}) {
        const SEARCH_PARAMETERS = searchParameters({
          filters: state.filters,
          sorts: state.sorts,
          statistics: state.statistics
        })

        // Map filters to new API format
        const mappedFilters = SEARCH_PARAMETERS.filters.map(filterParam => {
          // Find the original filter object from state to get displayName, type, etc.
          const originalFilter = state.filters.find(f => f.key === filterParam.key || f.field === filterParam.field) || {}

          // Get displayName - should be a multilingual object, or convert if it's a string
          let displayName = originalFilter.displayName
          if (typeof displayName === 'string') {
            // If displayName is a string, create a multilingual object with current language
            const currentLang = rootGetters['gui/getLanguage']
            displayName = { [currentLang]: displayName }
          } else if (!displayName || typeof displayName !== 'object') {
            // Fallback if displayName is missing
            displayName = {}
          }

          return {
            displayName: displayName,
            filterType: getFilterTypeToSearchNext(originalFilter.type || 'text'),
            values: filterParam.values,
            name: filterParam.key || filterParam.field,
            type: originalFilter.type || 'text'
          }
        })

        // Map sorts to new API format
        const mappedSorts = SEARCH_PARAMETERS.sorts
          ? SEARCH_PARAMETERS.sorts.map(sortParam => {
            // Find the original sort object from state to get additional properties
            const originalSort = state.sorts.find(s => s.field === sortParam.field) || {}

            return {
              isDescending: sortParam.descending !== undefined ? sortParam.descending : originalSort.isDescending || false,
              name: sortParam.field,
              type: 'string' // Default type, may need to be determined from field type
            }
          })
          : []

        const o = base.getOptions(options, {
          language: rootGetters['gui/getLanguage'],
          filters: mappedFilters,
          sort: mappedSorts,
          page: SEARCH_PARAMETERS.page.number,
          pageSize: SEARCH_PARAMETERS.page.size
        })

        commit('setLoading', { key: isKey, loading: true, initial: true })

        return new Promise(resolve => {
          http({
            method: 'post',
            url: `/${STOREMODULE_ENDPOINTMODULE_MAP.searchnext}/api/search/${serverEntityName}/query`,
            data: o.data
          })
            .then(response => {
              const mappedFilters = response.data.filters.map(filter => ({
                key: filter.name[i18n.locale],
                field: filter.name[i18n.locale],
                displayName: filter.label,
                type: getFilterTypeFromSearchNext(filter),
                sortOrder: filter.sortOrder,
                isInternal: filter.isHidden,
                items: filter.values?.map(itm => ({
                  key: itm.value,
                  text: itm.label[i18n.locale],
                  count: itm.count
                }))
              }))
              urlParameterKeySetter(mappedFilters)

              commit('setStatistics', response.data)
              commit('setFilters', mappedFilters)
              commit('setSorts', getSortSearchNext(response.data))
              commit('setHitColumns', response.data.hitColumns || [])
              commit(options.append ? 'append' : 'set', { key: entityKey, entities: response.data.result })
              commit('gui/setSearchEntityKey', entityKey, { root: true })
              resolve(getters.get)

              urlParameterSetter(filterParameters(state.filters))
            })
            .finally(() => {
              commit('setLoading', { key: isKey, loading: false })
            })
        })
      }
    },
    searchByQueryLimited (serverModuleName = '', serverEntityName = '', entityKey = '') {
      const isKey = `${serverEntityName}/performSearch`

      return function ({ state, getters, commit, dispatch, rootGetters }, options = {}) {
        const o = base.getOptions(options, {
          language: rootGetters['gui/getLanguage']
        })

        commit('setLoading', { key: isKey, loading: true, initial: true })

        return new Promise(resolve => {
          http({
            method: 'post',
            url: `/${STOREMODULE_ENDPOINTMODULE_MAP.searchnext}/api/${serverEntityName}/SearchByQueryLimited`,
            data: o.data
          })
            .then(response => {
              commit('setDecreasedEntities', response.data.result)
              resolve(getters.getDecreasedEntities)
            })
            .finally(() => {
              commit('setLoading', { key: isKey, loading: false })
            })
        })
      }
    },
    setFilters (serverModuleName = '', serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = { action: undefined }) {
        dispatch('resetPage', { action: false })

        if (options.action !== false) dispatch(options.action === undefined ? 'searchByFilters' : options.action)
      }
    },
    setActiveSort (serverModuleName = '', serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = { sortId: null, action: undefined }) {
        const oldActiveSort = state.sorts.find(s => s.isActive) || {}
        const newActiveSort = state.sorts.find(s => s.id === options.sortId) || {}

        if (newActiveSort.id) {
          if (oldActiveSort.id === newActiveSort.id) {
            newActiveSort.isDescending = !newActiveSort.isDescending
          } else {
            oldActiveSort.isActive = false
            newActiveSort.isActive = true
          }

          dispatch('resetPage', { action: false })
        }

        if (options.action !== false) dispatch(options.action === undefined ? 'searchByFilters' : options.action)
      }
    },
    getSearchPage (serverModuleName = '', serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = { number: undefined, action: undefined }) {
        if (options.number !== undefined) {
          state.statistics.page.number = options.number
          if (options.action !== false) dispatch(options.action !== undefined ? options.action : 'searchByFilters')
        }
      }
    },
    getNextSearchPage (serverModuleName = '', serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = { action: undefined }) {
        state.statistics.page.number += 1
        if (options.action !== false) dispatch(options.action !== undefined ? options.action : 'searchByFilters', { append: true })
      }
    },
    resetFilters (serverModuleName = '', serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = { action: undefined }) {
        if (searchParameters({ filters: state.filters }).filters.length) {
          state.filters.map(filter => Object.assign(filter, { controls: filterControlResetter(filter) }))
          dispatch('resetPage', { action: false })

          if (options.action !== false) dispatch(options.action === undefined ? 'searchByFilters' : options.action)
        }
      }
    },
    resetSorts (serverModuleName = '', serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = { action: undefined }) {
        state.sorts.map(sort => Object.assign(sort, { isActive: sort.isDefault }))
        dispatch('resetPage', { action: false })

        if (options.action !== false) dispatch(options.action === undefined ? 'searchByFilters' : options.action)
      }
    },
    resetPage (serverModuleName = '', serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = { action: undefined }) {
        state.statistics.page.number = PAGINATION_INITIAL_VALUES.page

        if (options.action !== false) dispatch(options.action === undefined ? 'searchByFilters' : options.action)
      }
    }
  }
}
